import { useEffect, useRef, useState } from 'react';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { useLanguage } from './LanguageContext';

const SYSTEM_PROMPT = `You are the official AI assistant for holol Club (نادي حلول), a premium consulting platform that connects investors and professionals with top-tier service providers worldwide.

holol Club offers 6 core service categories:
1. Legal Advisory — corporate formation, contracts, real estate law, labor law, IP, litigation (25 services)
2. Accounting & Audit — bookkeeping, taxation, CFO services, financial modeling, forensic accounting (25 services)
3. Financing Solutions — bank credit, venture capital, Islamic finance, government grants, debt restructuring (25 services)
4. Feasibility Studies — market analysis, SWOT, PESTEL, financial modeling, ROI/IRR, business plans (25 services)
5. Advertising & Marketing — branding, digital marketing, SEO, content production, PR, offline marketing (25 services)
6. Residence & Immigration — citizenship by investment, golden visas, entrepreneur visas, relocation support (25 services)

Your role:
- Answer questions about holol Club services clearly and professionally
- Help users identify which service fits their needs
- Be concise, warm, and helpful
- Support both English and Arabic — respond in the same language the user writes in
- Do not invent specific pricing or timelines; invite users to register or contact the team for detailed quotes
- Always end with a helpful next step (e.g., "You can register on our platform or contact our team for a free consultation.")

Keep responses under 200 words unless the question requires more detail.`;

function buildGenAI() {
  const key = process.env.REACT_APP_GEMINI_API_KEY;
  if (!key || key === 'your_gemini_api_key_here') return null;
  return new GoogleGenerativeAI(key);
}

const genAI = buildGenAI();

export function AIChatWidget() {
  const { language } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const chatRef = useRef(null);
  const inputRef = useRef(null);
  const bottomRef = useRef(null);
  const isAr = language === 'ar';

  const welcomeMessage = isAr
    ? 'مرحباً! أنا مساعد نادي حلول الذكي. كيف يمكنني مساعدتك اليوم؟'
    : 'Hello! I\'m the holol Club AI assistant. How can I help you today?';

  useEffect(() => {
    if (open) {
      setMessages(prev => prev.length === 0 ? [{ role: 'assistant', text: welcomeMessage }] : prev);
    }
  }, [open, welcomeMessage]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text }]);
    setLoading(true);

    if (!genAI) {
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: isAr
            ? 'لم يتم تكوين مفتاح Gemini API بعد. يُرجى إضافته في ملف .env.'
            : 'Gemini API key is not configured yet. Please add it to the .env file.',
        },
      ]);
      setLoading(false);
      return;
    }

    try {
      const model = genAI.getGenerativeModel({ model: 'gemini-2.5-flash-lite' });

      // Build history from prior messages, skipping the opening welcome message
      // Gemini requires history to alternate user/model starting with user
      const prior = messages.slice(1); // drop welcome assistant message
      const history = prior.map(m => ({
        role: m.role === 'user' ? 'user' : 'model',
        parts: [{ text: m.text }],
      }));

      const chat = model.startChat({
        history,
        systemInstruction: { role: 'system', parts: [{ text: SYSTEM_PROMPT }] },
      });
      const result = await chat.sendMessage(text);
      const reply = result.response.text();
      setMessages(prev => [...prev, { role: 'assistant', text: reply }]);
    } catch (err) {
      console.error('Gemini error:', err);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          text: isAr
            ? 'حدث خطأ. يُرجى المحاولة مرة أخرى.'
            : 'Something went wrong. Please try again.',
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <>
      {/* Chat panel */}
      {open && (
        <div
          dir={isAr ? 'rtl' : 'ltr'}
          ref={chatRef}
          className="fixed bottom-40 right-5 z-[59] w-80 sm:w-96 bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-outline-variant/20"
          style={{ maxHeight: '70vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 bg-primary text-white flex-shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center">
                <span className="material-symbols-outlined text-white text-xl">smart_toy</span>
              </div>
              <div>
                <p className="font-bold text-sm leading-tight">
                  {isAr ? 'مساعد نادي حلول الذكي' : 'holol AI Assistant'}
                </p>
                {/*<p className="text-white/60 text-xs">*/}
                {/*  {isAr ? 'مدعوم بـ Gemini' : 'Powered by Gemini'}*/}
                {/*</p>*/}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
              aria-label="Close"
            >
              <span className="material-symbols-outlined">close</span>
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-surface-container-lowest">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? (isAr ? 'justify-start' : 'justify-end') : (isAr ? 'justify-end' : 'justify-start')}`}
              >
                <div
                  className={`max-w-[80%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-secondary text-white rounded-br-sm'
                      : 'bg-white text-on-surface border border-outline-variant/20 rounded-bl-sm shadow-sm'
                  }`}
                >
                  {msg.text}
                </div>
              </div>
            ))}

            {loading && (
              <div className={`flex ${isAr ? 'justify-end' : 'justify-start'}`}>
                <div className="bg-white border border-outline-variant/20 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm flex gap-1 items-center">
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 rounded-full bg-secondary/60 animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          {/* Input */}
          <div className="flex items-end gap-2 px-4 py-3 border-t border-outline-variant/15 bg-white flex-shrink-0">
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              rows={1}
              placeholder={isAr ? 'اكتب سؤالك هنا...' : 'Ask me anything...'}
              className="flex-1 resize-none rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2.5 text-sm text-on-surface placeholder:text-on-surface/40 focus:outline-none focus:ring-2 focus:ring-secondary/50 max-h-28 overflow-y-auto"
              style={{ lineHeight: '1.5' }}
            />
            <button
              type="button"
              onClick={send}
              disabled={!input.trim() || loading}
              className="flex-shrink-0 w-10 h-10 rounded-xl bg-secondary text-white flex items-center justify-center hover:bg-secondary/85 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              <span className="material-symbols-outlined text-lg">send</span>
            </button>
          </div>
        </div>
      )}

      {/* Floating trigger button */}
      <button
        type="button"
        onClick={() => setOpen(prev => !prev)}
        aria-label={isAr ? 'فتح المساعد الذكي' : 'Open AI Assistant'}
        className="fixed bottom-24 right-5 z-[60] w-14 h-14 rounded-full bg-primary text-white shadow-[0_12px_28px_rgba(0,21,51,0.35)] flex items-center justify-center hover:scale-105 transition-transform"
      >
        <span className="material-symbols-outlined text-2xl">
          {open ? 'close' : 'smart_toy'}
        </span>
      </button>
    </>
  );
}
